import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';

import type { SimilarItemAlert } from '@/redux/modules/similarItemAlerts/similarItemAlerts.types';

import { SubtleMonochromeRouteLink } from '@/components/RouteLinks/RouteLinks';
import { useSimilarItemAlertRow } from '@/hooks/useSimilarItemAlertRow';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getItemUrl } from '@/utils/urls';

import { MySimilarItemAlertsTableRow } from './MySimilarItemAlertsTableRow';

type Props = {
    alert: SimilarItemAlert;
};

/**
 * One row of the For You "My Similar Item Alerts" tab: the saved lot, when it last triggered an
 * email, when the alert was set, and a link into the shared delete + feedback flow.
 *
 * The thumbnail and title link to the lot's PDP rather than the whole row being clickable — the row
 * also holds the Delete Alert control, and an anchor can't wrap a button.
 */
export const MySimilarItemAlertRow = ({ alert }: Props) => {
    const formatMessage = useAppFormatMessage();

    const { dateAdded, imageUrl, lastAlert, onDelete, title } = useSimilarItemAlertRow(alert);

    return (
        <MySimilarItemAlertsTableRow
            action={
                <InlineButton onClick={onDelete}>
                    {formatMessage(['similarItemAlerts', 'deleteAlertLabel'])}
                </InlineButton>
            }
            dateAdded={<BodySecondary>{dateAdded}</BodySecondary>}
            item={
                <SubtleMonochromeRouteLink
                    className="flex min-w-0 items-center gap-sm"
                    to={getItemUrl(alert.lotId, title)}
                >
                    {/* getItemSummaryFirstImageUrl falls back to the shared placeholder image, so
                        there is always something to render — no empty-square branch needed. */}
                    <img
                        alt={title}
                        className="size-[48px] shrink-0 rounded-xs object-cover"
                        src={imageUrl}
                    />
                    <div className="min-w-0">
                        <BodySecondary className="line-clamp-2">{title}</BodySecondary>
                        <Caption
                            className="tabletMin:hidden"
                            color="secondary"
                        >
                            {formatMessage(['similarItemAlerts', 'lastAlertMobile'], { values: { date: lastAlert } })}
                        </Caption>
                    </div>
                </SubtleMonochromeRouteLink>
            }
            lastAlert={<BodySecondary>{lastAlert}</BodySecondary>}
        />
    );
};
